"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { hideToast } from "../../redux/slices/toastSlice";
import { RxCross1 } from "react-icons/rx";
import { BsExclamationCircle } from "react-icons/bs";



export default function Toast({
  duration = 1500,  
  maxWidth = "max-w-lg",
}) {
  useEffect(() => {
    let el = document.getElementById("toast-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "toast-root";
      document.body.appendChild(el);
    }
  }, []);

  const dispatch = useDispatch();
  const isOpen = useSelector((s) => s.toast?.show);
  const message = useSelector((s) => s.toast?.message);

  const type=useSelector((s)=>s.toast?.type)

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const timerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    remainingRef.current = duration;
  }, [duration]);

  useEffect(() => {
    if (isOpen) {
      startRef.current = Date.now();
      pausedRef.current = false;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        handleClose();
      }, remainingRef.current);
    } else {
      clearTimeout(timerRef.current);
      remainingRef.current = duration;
    }

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [isOpen, duration]);

  const handleClose = () => {
    clearTimeout(timerRef.current);
    remainingRef.current = duration;
    dispatch(hideToast());
  };

  const pause = () => {
    if (!isOpen || pausedRef.current) return;
    pausedRef.current = true;
    const elapsed = Date.now() - (startRef.current || Date.now());
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimeout(timerRef.current);
  };

  const resume = () => {
    if (!isOpen || !pausedRef.current) return;
    pausedRef.current = false;
    startRef.current = Date.now();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => handleClose(), remainingRef.current);
  };


  if (typeof document === "undefined") return null;

  const DesktopToast = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="centered-toast"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          className="fixed inset-0  shadow-2xl drop-shadow-2xl z-50 flex items-center justify-center px-4"
          aria-live="polite"
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          <motion.div
            onMouseEnter={pause}
            onMouseLeave={resume}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className={`relative z-50 ${maxWidth} w-full  ${type==="success" ?"bg-white" :"  bg-red-50" } rounded-3xl shadow-xl p-6 md:p-8 flex flex-col items-center text-center`}
            style={{
              boxShadow:
                "0 12px 40px rgba(2,6,23,0.14), inset 0 1px 0 rgba(255,255,255,0.35)",
            }}
          >
            <div className="w-max mb-3">
              <div 
              className={`relative w-[56px] h-[56px] rounded-xl bg-white border-[1px] ${type==="success" ?" border-special text-special":" border-red-500 text-red-500"} flex items-center justify-center `}>
                <div className={`absolute left-3 -z-10 bottom-3 ${type==="success" ?" bg-special":" bg-red-500"}  w-[56px] h-[56px] rounded-xl`} />
                {
                  type==="success" &&
                  <FaCheck size={20} />

                }
                 {
                  type==="error" &&
                  <BsExclamationCircle size={30} />

                }
                
              </div>
            </div>
            

            <h3 className={`text-lg font-medium ${type==="success" ? "text-special" :"text-red-500" } mb-1`}>
              {message || "عملیات موفقیت آمیز بود"}
            </h3>
            <p className="text-sm text-gray-500 mb-0">{/* اختیاری */}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );


  const MobileToast = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="mobile-toast"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
          className="fixed inset-0   shadow-2xl drop-shadow-2xl pointer-events-none z-50 shad"
          aria-live="polite"
        >
          <div className="w-full h-full flex pointer-events-none items-end justify-center pb-6">
            <motion.div
              onMouseEnter={pause}
              onMouseLeave={resume}
              onClick={handleClose}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className={`pointer-events-auto ${maxWidth} ${type==="success" ?"bg-white" :"  bg-red-50" } rounded-2xl shadow-xl border border-gray-100
                         flex gap-4 items-center p-4 md:p-5 mx-4`}
              style={{
                boxShadow:
                  "0 12px 30px rgba(2,6,23,0.12), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${type==="success" ? "bg-special/10 text-special": " bg-white text-red-500"} `}>
              {
                  type==="success" &&
                  <FaCheck size={20} />

                }
                 {
                  type==="error" &&
                  <BsExclamationCircle size={20} />

                }
                

              </div>

              <div className="flex flex-col text-right">
                <div className="flex items-center gap-2">
                  <h4 className={`text-sm font-medium ${type==="success" ? "text-special" :"text-red-500" }`}>
                    {message || "عملیات موفق بود"}
                  </h4>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(
    isMobile ? MobileToast : DesktopToast,
    document.getElementById("toast-root") || document.body
  );
}
